import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, ArrowRight, RotateCcw } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatUSD = (n) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
    : '—';

export const ROICalculatorPage = () => {
  const [pieces, setPieces] = useState([]);
  const [pieceSlug, setPieceSlug] = useState('');
  const [year, setYear] = useState(new Date().getFullYear() - 5);
  const [price, setPrice] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/investments`);
        setPieces(res.data.pieces || []);
        if (res.data.pieces?.length) setPieceSlug(res.data.pieces[0].slug);
      } catch {
        setError('We could not load the Investment Index right now. Please refresh the page.');
      }
    })();
    window.scrollTo(0, 0);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/tools/roi`, {
        piece_slug: pieceSlug,
        purchase_year: parseInt(year, 10),
        purchase_price: parseFloat(price),
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not calculate ROI');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setPrice('');
    setError(null);
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2000; y--) years.push(y);

  return (
    <div data-testid="roi-calculator-page" className="bg-[#FAF9F6] min-h-screen">
      <SEO
        title="Pre-Owned Jewelry ROI Calculator — Cartier, Van Cleef, Tiffany Resale"
        description="Calculate the annual return on any tracked pre-owned luxury jewelry piece. Compare your purchase price to today's market resale value across our Investment Index."
        url="/tools/roi-calculator"
        type="website"
      />
      <SchemaOrg type="breadcrumb" data={{ items: [
        { name: 'Home', url: '/' },
        { name: 'Tools', url: '/tools' },
        { name: 'ROI Calculator', url: '/tools/roi-calculator' },
      ]}}/>

      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-12 lg:py-16 max-w-4xl">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-10">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/tools" className="hover:text-[#111] transition-colors">Tools</Link>
            <span>/</span>
            <span className="text-[#111]">ROI Calculator</span>
          </nav>
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-5 h-5 text-[#C69C6D]" strokeWidth={1.5} />
            <p className="overline overline-gold">Investment Tool</p>
          </div>
          <h1 className="font-heading font-light text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#111] mb-6">
            Pre-Owned Jewelry <span className="italic">ROI</span> Calculator
          </h1>
          <p className="text-lg text-[#555] leading-relaxed max-w-2xl">
            Enter what you bought, when, and what you paid. We&apos;ll compute your absolute gain, total return, and annualized rate against our live Investment Index.
          </p>
        </div>
      </section>

      <section className="section-editorial">
        <div className="container-editorial max-w-3xl">
          {result ? (
            <div data-testid="roi-result" className="bg-white border border-[#E5E0D8] p-8 lg:p-12">
              <div className="flex items-center gap-6 mb-10 pb-10 border-b border-[#E5E0D8]">
                <div className="w-24 h-24 lg:w-32 lg:h-32 overflow-hidden flex-shrink-0 bg-[#E5E0D8]">
                  <img src={result.piece.image_url} alt="" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="overline overline-gold mb-2">{result.piece.designer}</p>
                  <p className="font-heading text-2xl lg:text-3xl text-[#111] font-light leading-tight">{result.piece.name}</p>
                  <p className="text-sm text-[#888] mt-2">Editor rating: <span className="text-[#111]">{result.piece.editor_rating} · {result.piece.editor_verdict}</span></p>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E0D8] mb-10">
                <div className="bg-white p-5">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">Purchase Price</p>
                  <p className="font-heading text-2xl text-[#111]">{formatUSD(result.purchase_price)}</p>
                </div>
                <div className="bg-white p-5">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">Current Resale</p>
                  <p className="font-heading text-2xl text-[#111]">{formatUSD(result.current_resale)}</p>
                </div>
                <div className="bg-white p-5">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">Years Held</p>
                  <p className="font-heading text-2xl text-[#111]">{result.years_held}</p>
                </div>
                <div className="bg-white p-5">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">Absolute Gain</p>
                  <p className={`font-heading text-2xl ${result.absolute_gain >= 0 ? 'text-[#2F6B3F]' : 'text-[#9B2C2C]'}`}>
                    {result.absolute_gain >= 0 ? '+' : ''}{formatUSD(result.absolute_gain)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-px bg-[#E5E0D8] mb-10">
                <div className="bg-[#FAF6EE] p-6">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">Total Return</p>
                  <p className={`font-heading text-4xl font-light ${result.total_return_pct >= 0 ? 'text-[#2F6B3F]' : 'text-[#9B2C2C]'}`}>
                    {result.total_return_pct >= 0 ? '+' : ''}{result.total_return_pct}%
                  </p>
                </div>
                <div className="bg-[#FAF6EE] p-6">
                  <p className="text-[10px] tracking-wider uppercase text-[#888] mb-2">Annualized Return</p>
                  <p className={`font-heading text-4xl font-light ${result.annual_return_pct >= 0 ? 'text-[#2F6B3F]' : 'text-[#9B2C2C]'}`}>
                    {result.annual_return_pct >= 0 ? '+' : ''}{result.annual_return_pct}%
                  </p>
                </div>
              </div>

              <div className="bg-[#F3EFE6] p-6 lg:p-8 border-l-4" style={{ borderColor: result.verdict_color }}>
                <p className="overline mb-3">Editor&apos;s Note</p>
                <p className="text-[#111] leading-relaxed">{result.verdict}</p>
              </div>

              <div className="flex flex-wrap gap-4 mt-10">
                <button onClick={reset} data-testid="roi-reset" className="btn-editorial inline-flex items-center gap-3">
                  <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                  Run Another Calculation
                </button>
                <Link to={`/investments/${result.piece.slug}`} className="btn-editorial inline-flex items-center gap-3">
                  See Full Investment Profile <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-[#E5E0D8] p-8 lg:p-12 space-y-8" data-testid="roi-form">
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase text-[#888] mb-3">
                  Which piece did you buy?
                </label>
                <select
                  data-testid="roi-piece-select"
                  value={pieceSlug}
                  onChange={(e) => setPieceSlug(e.target.value)}
                  className="w-full px-4 py-3 bg-[#FAF9F6] border border-[#E5E0D8] focus:border-[#111] focus:outline-none text-[#111] font-heading text-lg"
                >
                  {pieces.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.designer} — {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-[#888] mb-3">
                    Year purchased
                  </label>
                  <select
                    data-testid="roi-year-select"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="w-full px-4 py-3 bg-[#FAF9F6] border border-[#E5E0D8] focus:border-[#111] focus:outline-none text-[#111] font-heading text-lg"
                  >
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-[#888] mb-3">
                    Price paid (USD)
                  </label>
                  <input
                    data-testid="roi-price-input"
                    type="number"
                    inputMode="decimal"
                    min="100"
                    step="50"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="e.g. 5400"
                    className="w-full px-4 py-3 bg-[#FAF9F6] border border-[#E5E0D8] focus:border-[#111] focus:outline-none text-[#111] font-heading text-lg"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-[#9B2C2C]/10 border border-[#9B2C2C]/30 p-4 text-sm text-[#9B2C2C]">
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="roi-submit"
                disabled={!pieceSlug || !price || submitting}
                className="btn-editorial inline-flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? 'Calculating…' : 'Calculate My ROI'}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ROICalculatorPage;
